import { supabase } from "@/lib/supabaseClient";
import { asErrorMessage } from "./mappers";

export type NamedPayrollBlockerAssignment = {
  assignment_id: string;
  run_id: string;
  return_item_id: string;
  company_id: string;
  assigned_resolver_user_id: string;
  assignment_status: string;
  assignment_reason: string | null;
  assigned_at: string;
  accepted_at: string | null;
  resolved_at: string | null;
  blocker_code: string;
  blocker_context: Record<string, unknown>;
  blocker_payload: Record<string, unknown>;
  blocker_fingerprint: string;
  exact_adapter_key: string;
  required_capability: string;
  repair_surface_key: string | null;
  exact_authority_now: boolean;
};

export type NamedPayrollNotification = {
  notification_id: string;
  company_id: string;
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
};

export async function getNamedPayrollBlockerAssignment(
  assignmentId: string
): Promise<NamedPayrollBlockerAssignment | null> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_payroll_blocker_assignment", {
      p_assignment_id: assignmentId,
    });
  if (error) throw new Error(asErrorMessage(error));
  const rows = Array.isArray(data) ? data : [];
  return (rows[0] as NamedPayrollBlockerAssignment | undefined) ?? null;
}

export async function acceptNamedPayrollBlockerAssignment(
  assignmentId: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("accept_payroll_blocker_assignment", {
      p_assignment_id: assignmentId,
    });
  if (error) throw new Error(asErrorMessage(error));
}

export async function resolveNamedPayrollBlocker(
  returnItemId: string,
  resolutionNote: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("resolve_payroll_blocker", {
      p_return_item_id: returnItemId,
      p_resolution_note: resolutionNote,
    });
  if (error) throw new Error(asErrorMessage(error));
}

export async function listMyNamedPayrollNotifications(
  limit = 20
): Promise<NamedPayrollNotification[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_my_named_payroll_blocker_notifications", {
      p_limit: limit,
    });
  if (error) throw new Error(asErrorMessage(error));
  return Array.isArray(data) ? (data as NamedPayrollNotification[]) : [];
}

export async function markNamedPayrollNotificationRead(
  notificationId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("mark_my_named_payroll_blocker_notification_read", {
      p_notification_id: notificationId,
    });
  if (error) throw new Error(asErrorMessage(error));
  return Boolean(data);
}

export function buildNamedNotificationRoute(
  notification: NamedPayrollNotification
): string | null {
  const payload = notification.payload ?? {};
  const companyId = String(payload.company_id ?? notification.company_id ?? "");
  const runId = String(payload.run_id ?? "");
  const assignmentId = String(payload.assignment_id ?? "");
  const routeKey = String(payload.route_key ?? "");
  if (!companyId || !runId) return null;
  if (routeKey === "payroll_blocker_repair" && assignmentId) {
    return `/owner/company/${companyId}/payroll/run/${runId}/repair/${assignmentId}`;
  }
  if (routeKey === "payroll_issuer_review") {
    return `/owner/company/${companyId}/payroll/run/${runId}/review`;
  }
  return null;
}



export type IssuanceHandoffBlocker = {
  blocker_code: string | null;
  blocker_context: Record<string, unknown> | null;
  blocker_payload: Record<string, unknown>;
  resolution_route: string;
  employee_profile_id: string | null;
  field_key: string | null;
};

export type IssuanceHandoff = {
  run_id: string;
  company_id: string;
  readiness_status: "ready" | "blocked" | "unavailable";
  can_issue: boolean;
  blockers: IssuanceHandoffBlocker[];
  operational_incident_reference: string | null;
  message: string;
};

export type RectifierCandidate = {
  actor_user_id: string;
  candidate_label: string;
  candidate_email: string | null;
  candidate_basis: string;
  exact_authority: boolean;
};

export type TimeCorrectionItem = {
  employee_profile_id: string;
  correction_key:
    | "timesheet_row"
    | "clock_in"
    | "clock_out"
    | "worked_hours"
    | "break_duration"
    | "time_dispute";
  correction_note: string;
};

export async function preparePayrollRunIssuanceHandoff(
  runId: string
): Promise<IssuanceHandoff> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("prepare_payroll_run_issuance_handoff", { p_run_id: runId });
  if (error) throw new Error(asErrorMessage(error));
  return data as IssuanceHandoff;
}

export async function previewNamedRectifierCandidates(params: {
  runId: string;
  blockerCode: string;
  blockerContext: Record<string, unknown>;
}): Promise<RectifierCandidate[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("preview_payroll_blocker_rectifier_candidates_ui", {
      p_run_id: params.runId,
      p_blocker_code: params.blockerCode,
      p_context: params.blockerContext,
    });
  if (error) throw new Error(asErrorMessage(error));
  return Array.isArray(data) ? (data as RectifierCandidate[]) : [];
}

export async function assignNamedRectifier(params: {
  runId: string;
  blockerCode: string;
  blockerContext: Record<string, unknown>;
  blockerPayload: Record<string, unknown>;
  resolverUserId: string;
  reason: string;
}): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("assign_payroll_blocker_rectifier", {
      p_run_id: params.runId,
      p_blocker_code: params.blockerCode,
      p_context: params.blockerContext,
      p_blocker_payload: params.blockerPayload,
      p_resolver_user_id: params.resolverUserId,
      p_reason: params.reason,
    });
  if (error) throw new Error(asErrorMessage(error));
  return (data ?? {}) as Record<string, unknown>;
}

export async function returnRunForTimeCorrection(params: {
  runId: string;
  reason: "TIME_DATA_INCOMPLETE" | "TIME_DATA_ANOMALY";
  items: TimeCorrectionItem[];
  note: string;
}): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("return_run_for_time_correction", {
      p_run_id: params.runId,
      p_reason: params.reason,
      p_items: params.items,
      p_note: params.note,
    });
  if (error) throw new Error(asErrorMessage(error));
  return (data ?? {}) as Record<string, unknown>;
}
